import { useRef } from 'react';
import {
  ApolloProvider as BaseApolloProvider,
  type ApolloClient,
  from,
  type NormalizedCacheObject,
} from '@apollo/client';
import { useLocalisationContext } from '@olxpwa-lib/i18n/client';
import { useHostProps } from '~features/host-props';
import { createApolloClient } from './apollo-client';
import { useAuthLink, useErrorLink, useHttpLink, useRestLink, useAPQLink } from './links';

export type ApolloProviderProps = {
  children: React.ReactNode;
  ssrClient?: ApolloClient<NormalizedCacheObject>;
  initialCache?: NormalizedCacheObject;
};

export const ApolloProvider: React.FC<ApolloProviderProps> = ({
  children,
  ssrClient,
  initialCache,
}) => {
  const { restAPIBaseURL, prefetchedQueries } = useHostProps();
  const {
    selectedLanguage: { langISOCode },
  } = useLocalisationContext();
  const authLink = useAuthLink();
  const errorLink = useErrorLink();
  const httpLink = useHttpLink();
  const apqLink = useAPQLink();
  const restLink = useRestLink({ baseUrl: restAPIBaseURL, langISOCode });
  const client = useRef(
    createApolloClient({
      link: from([authLink, apqLink, errorLink, restLink, httpLink]),
      initialCache,
      prefetchedQueries,
    }),
  );

  return <BaseApolloProvider client={ssrClient ?? client.current}>{children}</BaseApolloProvider>;
};

export function withApolloProvider<T extends {}>(
  Component: React.ComponentType<T>,
  providerProps?: Omit<ApolloProviderProps, 'children'>,
): React.ComponentType<T> {
  return (props) => (
    <ApolloProvider {...providerProps}>
      <Component {...props} />
    </ApolloProvider>
  );
}
